import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AdminUser, Role } from '@hrlabs/shared-types';
import { createAdminUser, listAdminUsers, setUserRoleClaim } from '../../lib/functions';
import { useToast } from '../../components/Toast';

export function UserEdit() {
  const { uid } = useParams();
  const isNew = !uid;
  const navigate = useNavigate();
  const { notify } = useToast();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<AdminUser | null>(null);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('moderator');

  useEffect(() => {
    if (isNew) return;
    listAdminUsers().then((res) => {
      const u = res.data.users.find((x) => x.uid === uid) ?? null;
      setExisting(u);
      if (u) {
        setEmail(u.email);
        setDisplayName(u.displayName ?? '');
        setRole(u.role);
      }
      setLoading(false);
    });
  }, [isNew, uid]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        if (password.length < 6) {
          notify('danger', 'Password must be at least 6 characters.');
          setSaving(false);
          return;
        }
        await createAdminUser({ email: email.trim(), password, displayName: displayName.trim(), role });
        notify('success', 'User created.');
      } else {
        await setUserRoleClaim({ uid: uid!, role });
        notify('success', 'Role updated.');
      }
      navigate('/users');
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 480 }}>
      <h3 className="fw-bold mb-3">{isNew ? 'New user' : `Edit ${existing?.email ?? ''}`}</h3>
      <div className="mb-3">
        <label className="form-label fw-semibold">Email</label>
        <input
          type="email"
          className="form-control"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={!isNew}
        />
      </div>
      {isNew && (
        <>
          <div className="mb-3">
            <label className="form-label fw-semibold">Display name</label>
            <input
              className="form-control"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Password (min 6)</label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
        </>
      )}
      <div className="mb-3">
        <label className="form-label fw-semibold">Role</label>
        <select className="form-select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          <option value="admin">admin</option>
          <option value="moderator">moderator</option>
        </select>
      </div>
      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : isNew ? 'Create' : 'Update role'}
      </button>
    </form>
  );
}
